import type { GHGInventoryResult } from '../components/charts/ghgInventoryChartTypes';

export function isWastewaterChartLabel(label: string): boolean {
  return label === 'Wastewater' || label.startsWith('Wastewater ');
}

/** Label for stacked/line wastewater series - from canonical chart dataset when present. */
export function wastewaterSeriesLabelFromGhgData(
  ghgData?: GHGInventoryResult | null
): string {
  const datasets = ghgData?.chartData?.datasets as Array<{ label?: string }> | undefined;
  const match = datasets?.find((d) => isWastewaterChartLabel(String(d.label ?? '')));
  return match?.label ? String(match.label) : 'Wastewater';
}

const TIME_SERIES_BASE_LABELS = [
  'Light Duty Vehicles',
  'Heavy Duty Vehicles',
  'Natural Gas - Residential',
  'Natural Gas - Commercial',
  'Electricity - Residential',
  'Electricity - Commercial',
  'Solid Waste',
] as const;

/** Legend labels for the active stacked-area or line chart (includes derived wastewater label). */
export function buildTimeSeriesChartSeriesLabels(
  ghgData: GHGInventoryResult | null | undefined,
  hasWastewater: boolean
): string[] {
  const labels: string[] = [...TIME_SERIES_BASE_LABELS];
  if (hasWastewater) {
    labels.push(wastewaterSeriesLabelFromGhgData(ghgData));
  }
  labels.push('Offroad');
  return labels;
}

/** Pie chart labels for the selected inventory year (from chart datasets when available). */
export function buildPieChartSeriesLabels(
  ghgData: GHGInventoryResult | null | undefined,
  pieYear = 2023
): string[] {
  if (!ghgData?.chartData?.datasets?.length || !ghgData.chartData.labels?.length) {
    return [];
  }
  const yearArr = (ghgData.chartData.labels as unknown[]).map((y) => Number(y));
  let idx = yearArr.indexOf(pieYear);
  if (idx < 0) idx = Math.max(0, yearArr.length - 1);

  return (ghgData.chartData.datasets as Array<{ label?: string; data?: unknown[] }>)
    .map((ds) => {
      const label = String(ds.label ?? '');
      const raw = ds.data?.[idx];
      if (isWastewaterChartLabel(label)) {
        const v = raw == null || raw === '' ? null : Number(raw);
        if (v == null || !Number.isFinite(v)) return null;
        return label;
      }
      const v = Number(raw) || 0;
      return v > 0 ? label : null;
    })
    .filter((label): label is string => label != null);
}

export function activeChartSeriesLabels(
  chartType: 'stacked-area' | 'line' | 'pie',
  ghgData: GHGInventoryResult | null | undefined,
  hasWastewater: boolean
): string[] {
  if (chartType === 'pie') {
    return buildPieChartSeriesLabels(ghgData);
  }
  return buildTimeSeriesChartSeriesLabels(ghgData, hasWastewater);
}
